import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  MessageSquare, 
  ArrowLeft,
  Loader2,
  Check,
  X,
  ExternalLink,
  Link2,
  Unlink,
  RefreshCw,
  Settings,
  ChevronRight,
  Database,
  Shield,
  Zap,
  FileText,
  AlertCircle,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import CMSSetupWizard from '@/components/CMSSetupWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CMS logos/icons mapping
const CMS_ICONS = {
  filevine: '📁',
  clio: '⚖️',
  practicepanther: '🐆',
  mycase: '📋',
  smokeball: '💨',
  litify: '⚡',
  casepeer: '👥',
  smartadvocate: '🧠',
  lawmatics: '📊',
  neos: '🎯',
  cloudlex: '☁️',
  prevail: '🏆'
};

const CMSIntegrationsPage = () => {
  const {  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [systems, setSystems] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [connectingCms, setConnectingCms] = useState(null);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [selectedCms, setSelectedCms] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [wizardCms, setWizardCms] = useState(null);
  const [syncSettings, setSyncSettings] = useState({
    auto_sync: true,
    sync_contacts: true,
    sync_documents: true,
    sync_case_summary: true,
    create_matter: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [systemsRes, integrationsRes] = await Promise.all([
        axios.get(`${API}/cms/systems`),
        axios.get(`${API}/cms/integrations`)
      ]);
      setSystems(systemsRes.data.systems);
      setIntegrations(integrationsRes.data.integrations);
    } catch (error) {
      console.error('Failed to fetch CMS data:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const getIntegrationStatus = (cmsType) => {
    return integrations.find(i => i.cms_type === cmsType);
  };

  const handleShowWizard = (cms) => {
    setWizardCms(cms);
    setShowSetupWizard(true);
  };

  const handleWizardComplete = () => {
    setShowSetupWizard(false);
    // After wizard, show connect dialog
    if (wizardCms) {
      handleConnect(wizardCms);
    }
  };

  const handleConnect = (cms) => {
    setSelectedCms(cms);
    setApiKey('');
    setApiSecret('');
    setWebhookUrl('');
    
    if (cms.auth_type === 'oauth') {
      // For OAuth, redirect to auth URL
      handleOAuthConnect(cms);
    } else {
      // For API key or webhook, show dialog
      setShowConnectDialog(true);
    }
  };

  const handleOAuthConnect = async (cms) => {
    setConnectingCms(cms.id);
    try {
      const response = await axios.get(`${API}/cms/oauth/url/${cms.id}`);
      
      // Show instructions since we need app registration
      toast.info(
        `To connect ${cms.name}, you'll need to register IntakePal in their developer portal first.`,
        { duration: 5000 }
      );
      
      // Open docs in new tab
      if (cms.docs_url) {
        window.open(cms.docs_url, '_blank');
      }
    } catch (error) {
      console.error('OAuth URL error:', error);
      toast.error('Failed to initiate OAuth');
    } finally {
      setConnectingCms(null);
    }
  };

  const handleConnectSubmit = async () => {
    if (!selectedCms) return;
    
    setConnecting(true);
    try {
      const payload = {
        cms_type: selectedCms.id
      };
      
      if (selectedCms.auth_type === 'api_key') {
        if (!apiKey) {
          toast.error('API key is required');
          setConnecting(false);
          return;
        }
        payload.api_key = apiKey;
        if (apiSecret) payload.api_secret = apiSecret;
      } else if (selectedCms.auth_type === 'zapier') {
        if (!webhookUrl) {
          toast.error('Webhook URL is required');
          setConnecting(false);
          return;
        }
        payload.webhook_url = webhookUrl;
      }
      
      await axios.post(`${API}/cms/connect/${selectedCms.id}`, payload);
      
      toast.success(`Connected to ${selectedCms.name}!`);
      setShowConnectDialog(false);
      fetchData();
    } catch (error) {
      console.error('Connect error:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (cmsType, cmsName) => {
    if (!window.confirm(`Are you sure you want to disconnect ${cmsName}? This will stop automatic syncing.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/cms/disconnect/${cmsType}`);
      toast.success(`Disconnected from ${cmsName}`);
      fetchData();
    } catch (error) {
      console.error('Disconnect error:', error);
      toast.error('Failed to disconnect');
    }
  };

  const handleOpenSettings = (integration) => {
    setSelectedIntegration(integration);
    setSyncSettings(integration.sync_settings || {
      auto_sync: true,
      sync_contacts: true,
      sync_documents: true,
      sync_case_summary: true,
      create_matter: true
    });
    setShowSettingsDialog(true);
  };

  const handleSaveSettings = async () => {
    if (!selectedIntegration) return;
    
    try {
      await axios.put(
        `${API}/cms/sync-settings/${selectedIntegration.cms_type}`,
        syncSettings,
        
      );
      toast.success('Settings saved');
      setShowSettingsDialog(false);
      fetchData();
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error('Failed to save settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  const connectedCount = integrations.filter(i => i.connected).length;
  const piSystems = systems.filter(s => s.pi_specific);
  const generalSystems = systems.filter(s => !s.pi_specific);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/integrations">
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  IntakePal
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Case Management Integrations
          </h1>
          <p className="text-slate-600">
            Connect IntakePal to your case management system to automatically sync leads and create new cases.
          </p>
          {connectedCount > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
              <Check className="w-4 h-4" />
              {connectedCount} system{connectedCount !== 1 ? 's' : ''} connected
            </div>
          )}
        </div>

        {/* How it Works */}
        <Card className="mb-8 border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-teal-600" />
              How Auto-Sync Works
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                <div>
                  <p className="font-medium text-slate-900">New Lead Submitted</p>
                  <p className="text-sm text-slate-600">Client fills out intake form</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                <div>
                  <p className="font-medium text-slate-900">System Prioritizes Lead</p>
                  <p className="text-sm text-slate-600">Summary & score generated</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                <div>
                  <p className="font-medium text-slate-900">Case Created</p>
                  <p className="text-sm text-slate-600">Auto-synced to your CMS</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PI-Specific Systems */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            Personal Injury Focused
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {piSystems.map(cms => {
              const integration = getIntegrationStatus(cms.id);
              const isConnected = integration?.connected;
              
              return (
                <Card 
                  key={cms.id} 
                  className={`transition-all ${isConnected ? 'border-emerald-300 bg-emerald-50/50' : 'hover:border-slate-300'}`}
                  data-testid={`cms-card-${cms.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{CMS_ICONS[cms.id] || '📁'}</span>
                        <div>
                          <h3 className="font-semibold text-slate-900">{cms.name}</h3>
                          <p className="text-xs text-slate-500 capitalize">{cms.auth_type === 'zapier' ? 'Zapier' : cms.auth_type.replace('_', ' ')}</p>
                        </div>
                      </div>
                      {isConnected && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                          <Check className="w-3 h-3" />
                          Connected
                        </span>
                      )}
                    </div>
                    
                    {isConnected ? (
                      <div className="space-y-2">
                        <div className="text-xs text-slate-500">
                          {integration.sync_stats?.total_synced || 0} cases synced
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => handleOpenSettings(integration)}
                            data-testid={`settings-btn-${cms.id}`}
                          >
                            <Settings className="w-3 h-3 mr-1" />
                            Settings
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDisconnect(cms.id, cms.name)}
                            data-testid={`disconnect-btn-${cms.id}`}
                          >
                            <Unlink className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {cms.note && (
                          <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            {cms.note}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleShowWizard(cms)}
                            data-testid={`guide-btn-${cms.id}`}
                          >
                            <BookOpen className="w-3 h-3 mr-1" />
                            Setup Guide
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1 bg-teal-500 hover:bg-teal-600"
                            onClick={() => handleConnect(cms)}
                            disabled={connectingCms === cms.id}
                            data-testid={`connect-btn-${cms.id}`}
                          >
                            {connectingCms === cms.id ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Link2 className="w-3 h-3 mr-1" />
                            )}
                            Connect
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* General Practice Systems */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-slate-600" />
            General Practice Management
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {generalSystems.map(cms => {
              const integration = getIntegrationStatus(cms.id);
              const isConnected = integration?.connected;
              
              return (
                <Card 
                  key={cms.id} 
                  className={`transition-all ${isConnected ? 'border-emerald-300 bg-emerald-50/50' : 'hover:border-slate-300'}`}
                  data-testid={`cms-card-${cms.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{CMS_ICONS[cms.id] || '📁'}</span>
                        <div>
                          <h3 className="font-semibold text-slate-900">{cms.name}</h3>
                          <p className="text-xs text-slate-500 capitalize">{cms.auth_type === 'zapier' ? 'Zapier' : cms.auth_type.replace('_', ' ')}</p>
                        </div>
                      </div>
                      {isConnected && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                          <Check className="w-3 h-3" />
                          Connected
                        </span>
                      )}
                    </div>
                    
                    {isConnected ? (
                      <div className="space-y-2">
                        <div className="text-xs text-slate-500">
                          {integration.sync_stats?.total_synced || 0} cases synced
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => handleOpenSettings(integration)}
                          >
                            <Settings className="w-3 h-3 mr-1" />
                            Settings
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDisconnect(cms.id, cms.name)}
                          >
                            <Unlink className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleShowWizard(cms)}
                        >
                          <BookOpen className="w-3 h-3 mr-1" />
                          Guide
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-teal-500 hover:bg-teal-600"
                          onClick={() => handleConnect(cms)}
                          disabled={connectingCms === cms.id}
                        >
                          {connectingCms === cms.id ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Link2 className="w-3 h-3 mr-1" />
                          )}
                          Connect
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Help Section */}
        <Card className="mt-8 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-slate-400 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Need Help?</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Each case management system has different setup requirements. For OAuth-based systems, 
                  you&apos;ll need to register IntakePal as an application in their developer portal first.
                </p>
                <a 
                  href="mailto:support@intakepal.com" 
                  className="text-sm font-medium text-teal-600 hover:text-teal-700"
                >
                  Contact support for integration assistance →
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Connect Dialog (for API key and Zapier) */}
      <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{selectedCms && CMS_ICONS[selectedCms.id]}</span>
              Connect {selectedCms?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedCms?.auth_type === 'api_key' 
                ? `Enter your ${selectedCms?.name} API credentials to enable automatic syncing.`
                : `Enter your Zapier webhook URL to receive intake data.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedCms?.auth_type === 'api_key' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="apiKey">API Key *</Label>
                  <Input
                    id="apiKey"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your API key"
                    data-testid="api-key-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apiSecret">API Secret (optional)</Label>
                  <Input
                    id="apiSecret"
                    type="password"
                    value={apiSecret}
                    onChange={(e) => setApiSecret(e.target.value)}
                    placeholder="Enter your API secret if required"
                  />
                </div>
              </>
            )}
            
            {selectedCms?.auth_type === 'zapier' && (
              <div className="space-y-2">
                <Label htmlFor="webhookUrl">Zapier Webhook URL *</Label>
                <Input
                  id="webhookUrl"
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://hooks.zapier.com/..."
                  data-testid="webhook-url-input"
                />
                <p className="text-xs text-slate-500">
                  Create a Zap with &quot;Webhooks by Zapier&quot; as the trigger to get this URL.
                </p>
              </div>
            )}
            
            {selectedCms?.docs_url && (
              <a 
                href={selectedCms.docs_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
              >
                View {selectedCms.name} API docs
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConnectSubmit} 
              disabled={connecting}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="confirm-connect-btn"
            >
              {connecting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-teal-600" />
              Sync Settings - {selectedIntegration?.cms_name}
            </DialogTitle>
            <DialogDescription>
              Configure what data syncs to your case management system.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-sync new leads</Label>
                <p className="text-xs text-slate-500">Automatically create cases for new intakes</p>
              </div>
              <Switch
                checked={syncSettings.auto_sync}
                onCheckedChange={(checked) => setSyncSettings(s => ({ ...s, auto_sync: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Sync contact info</Label>
                <p className="text-xs text-slate-500">Name, email, phone number</p>
              </div>
              <Switch
                checked={syncSettings.sync_contacts}
                onCheckedChange={(checked) => setSyncSettings(s => ({ ...s, sync_contacts: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Sync documents</Label>
                <p className="text-xs text-slate-500">Photos, police reports, medical docs</p>
              </div>
              <Switch
                checked={syncSettings.sync_documents}
                onCheckedChange={(checked) => setSyncSettings(s => ({ ...s, sync_documents: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Sync case summary</Label>
                <p className="text-xs text-slate-500">Include automated case analysis</p>
              </div>
              <Switch
                checked={syncSettings.sync_case_summary}
                onCheckedChange={(checked) => setSyncSettings(s => ({ ...s, sync_case_summary: checked }))}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label>Create new matter/case</Label>
                <p className="text-xs text-slate-500">Automatically create a new case record</p>
              </div>
              <Switch
                checked={syncSettings.create_matter}
                onCheckedChange={(checked) => setSyncSettings(s => ({ ...s, create_matter: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSettings}
              className="bg-teal-500 hover:bg-teal-600"
            >
              <Check className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Wizard */}
      <CMSSetupWizard
        cms={wizardCms}
        isOpen={showSetupWizard}
        onClose={() => setShowSetupWizard(false)}
        onComplete={handleWizardComplete}
      />
    </div>
  );
};

export default CMSIntegrationsPage;
